/**
 * The return page's polling policy, as plain functions.
 *
 * This is the money path: it decides when the shopper is told their payment
 * landed, and when we give up and send them to support. It lives outside the
 * component so those decisions can be tested against the clock without
 * mounting React and without a fake `fetch` — see ./poll.test.ts.
 *
 * The schedule and the settle rules are ported VERBATIM from the monorepo's
 * return-client.tsx. Do not "improve" them here; a consumer app's entitlement
 * grant is on the other end of this loop.
 */

import type { SessionStatus } from "./session-status";

/** First wait, before the second request. The first request is immediate. */
export const INITIAL_INTERVAL_MS = 2_000;
/** Ceiling on the wait, so a slow webhook is still checked every 8s. */
export const MAX_INTERVAL_MS = 8_000;
/** Gentle geometric back-off between those two bounds. */
export const BACKOFF_FACTOR = 1.3;
/** Total budget. Past this the page stops asking and shows a support hint. */
export const TIMEOUT_MS = 120_000;

/**
 * The wait to use after one that lasted `intervalMs`.
 *
 * Confirmation normally lands within a few seconds, so the first few requests
 * are close together; the back-off exists so a session stuck behind a delayed
 * webhook is not hammering the Worker sixty times a minute for two minutes.
 */
export function backOff(intervalMs: number): number {
  return Math.min(intervalMs * BACKOFF_FACTOR, MAX_INTERVAL_MS);
}

/**
 * Whether the give-up budget is spent.
 *
 * Checked BEFORE scheduling each request rather than before issuing it, which
 * is what the source did — so the last request may fire at any moment up to
 * the boundary, and the page never sits idle inside its own budget.
 */
export function isExhausted(elapsedMs: number): boolean {
  return elapsedMs >= TIMEOUT_MS;
}

/**
 * Every wait the page performs, in order, if nothing ever settles — the
 * schedule as a value, so a test can assert the shape of the whole thing
 * rather than re-deriving it one `backOff` call at a time.
 *
 * Modelled with instantaneous requests, which is what makes it a schedule
 * rather than a trace: real request latency pushes the wall clock out and the
 * live loop simply runs out of budget an entry or two sooner.
 */
export function pollDelaySchedule(): readonly number[] {
  const delays: number[] = [];
  let elapsed = 0;
  let interval: number = INITIAL_INTERVAL_MS;
  while (!isExhausted(elapsed)) {
    delays.push(interval);
    elapsed += interval;
    interval = backOff(interval);
  }
  return delays;
}

/** What one status response tells the loop to do next. */
export type PollDecision =
  | { readonly kind: "paid"; readonly receipt: string }
  | { readonly kind: "expired" }
  | { readonly kind: "abandoned" }
  | { readonly kind: "wait" };

/**
 * Read one status response.
 *
 * The rule that matters, and the one most likely to be broken by a
 * well-meaning edit: `status: "paid"` WITHOUT a receipt is NOT done. The
 * Stripe webhook — not this redirect — is the source of truth for a payment,
 * and the receipt is what the webhook issues. A "paid" row with no receipt
 * means we caught the session between those two facts. Settling there would
 * hand the consumer app a return URL carrying no receipt, and the consumer
 * verifies the receipt before granting anything — so the shopper would be
 * bounced back to a still-locked product having genuinely paid for it.
 *
 * `null` (an unparseable or unrecognised body) also waits, deliberately: the
 * only conclusions this function draws are ones it positively recognised.
 */
export function decide(status: SessionStatus | null): PollDecision {
  if (status === null) return { kind: "wait" };
  if (status.status === "paid") {
    return status.receipt !== undefined
      ? { kind: "paid", receipt: status.receipt }
      : { kind: "wait" };
  }
  if (status.status === "expired") return { kind: "expired" };
  if (status.status === "abandoned") return { kind: "abandoned" };
  return { kind: "wait" };
}

/**
 * How long the confirmed state is held before the browser leaves for the
 * consumer app. Long enough to read "Payment confirmed" and register that the
 * money arrived; short enough that nobody reaches for the back button.
 */
export const REDIRECT_DELAY_MS = 1_200;
